import type {
  AvaliacaoDoCliente,
  NovoPedidoValido,
  Pedido,
  PedidoDaAnalise,
  StatusPedido,
} from '@entregas/core';
import type { Db } from './cliente';
import { ErroApi, exigir, exigirOuNulo } from './erros';
import { inserirOuReler } from './idempotencia';
import { paraPagamentos, paraPedido } from './mapeadores';

/** Pedido com os nomes que as listas mostram (sem segunda consulta por linha). */
export interface PedidoNaLista extends Pedido {
  clienteNome: string;
  clienteTelefone: string;
  entregadorNome: string | null;
}

const COLUNAS_LISTA = '*, clientes(nome, telefone), entregadores(nome)';

type LinhaLista = Parameters<typeof paraPedido>[0] & {
  clientes: { nome: string; telefone: string } | null;
  entregadores: { nome: string } | null;
};

function paraPedidoNaLista(l: LinhaLista): PedidoNaLista {
  return {
    ...paraPedido(l),
    clienteNome: l.clientes?.nome ?? '',
    clienteTelefone: l.clientes?.telefone ?? '',
    entregadorNome: l.entregadores?.nome ?? null,
  };
}

/**
 * Cria o pedido. Com `id` (uuid gerado pelo painel antes de enviar e repetido no "Tentar de novo") é idempotente:
 * se a 1ª tentativa gravou e a resposta se perdeu, a 2ª bate na chave primária e volta o pedido que já está lá,
 * como está (nunca sobrescreve). Id de outra loja: o RLS esconde a linha e o erro do insert sai (decisão 117).
 */
export async function criarPedido(
  db: Db,
  empresaId: string,
  dados: NovoPedidoValido & {
    /** Id da tentativa (idempotência). Sem ele, o banco gera. */
    id?: string;
    destino?: { lat: number; lng: number } | null;
    /** Já sai com entregador escolhido (opcional; sem ele, qualquer um pega no app). */
    entregadorId?: string | null;
  },
): Promise<Pedido> {
  const { id } = dados;
  const linha = await inserirOuReler(
    await db
      .from('pedidos')
      .insert({
        ...(id && { id }),
        empresa_id: empresaId,
        cliente_id: dados.clienteId,
        endereco_id: dados.enderecoId,
        endereco_texto: dados.enderecoTexto,
        destino_lat: dados.destino?.lat ?? null,
        destino_lng: dados.destino?.lng ?? null,
        itens: dados.itens,
        valor_centavos: dados.valorCentavos,
        taxa_entrega_centavos: dados.taxaEntregaCentavos,
        forma_pagamento: dados.pagamentos[0]?.forma ?? 'pix',
        pagamentos: dados.pagamentos.map((parte) => ({
          forma: parte.forma,
          valor_centavos: parte.valorCentavos,
          parcelas: parte.parcelas,
        })),
        troco_para_centavos: dados.trocoParaCentavos,
        entregador_id: dados.entregadorId ?? null,
        observacao: dados.observacao,
        // `numero` é definido pelo trigger do banco.
        numero: 0,
      })
      .select()
      .single(),
    id
      ? async () =>
          exigirOuNulo(
            await db
              .from('pedidos')
              .select()
              .eq('id', id)
              .eq('empresa_id', empresaId)
              .maybeSingle(),
          )
      : null,
  );
  return paraPedido(linha);
}

/** Pedidos de um intervalo (normalmente o dia operacional), mais novos primeiro. */
export async function listarPedidos(
  db: Db,
  filtro: { inicio: string; fim: string; status?: StatusPedido[]; entregadorId?: string },
): Promise<PedidoNaLista[]> {
  let consulta = db
    .from('pedidos')
    .select(COLUNAS_LISTA)
    .gte('criado_em', filtro.inicio)
    .lt('criado_em', filtro.fim);
  if (filtro.status?.length) consulta = consulta.in('status', filtro.status);
  if (filtro.entregadorId) consulta = consulta.eq('entregador_id', filtro.entregadorId);

  const linhas = exigir(await consulta.order('criado_em', { ascending: false }).limit(500));
  return linhas.map(paraPedidoNaLista);
}

/**
 * Pedidos do acerto de um dia operacional: os concluídos (entregue ou não entregue) na janela, pela
 * hora da conclusão — a mesma base do `dias_de_entrega` do banco —, mais os que estão a caminho.
 * Filtrar por `criado_em` deixaria de fora o pedido feito antes da virada e entregue depois.
 */
export async function listarPedidosDoAcerto(
  db: Db,
  janela: { inicio: string; fim: string },
): Promise<PedidoNaLista[]> {
  // Entre aspas: o instante ISO tem "." e ":", que o filtro `or` do PostgREST reserva.
  const concluidosNaJanela = `and(status.in.(entregue,nao_entregue),concluido_em.gte."${janela.inicio}",concluido_em.lt."${janela.fim}")`;
  const linhas = exigir(
    await db
      .from('pedidos')
      .select(COLUNAS_LISTA)
      .or(`${concluidosNaJanela},status.eq.a_caminho`)
      .order('criado_em', { ascending: false })
      .limit(500),
  );
  return linhas.map(paraPedidoNaLista);
}

const PAGINA_ANALISE = 1000;

/**
 * Pedidos de um período longo (semana/mês) para a análise. Vem em páginas de 1000 — o limite
 * de linhas por resposta do Supabase — e só com o que a conta precisa.
 */
export async function listarPedidosDaAnalise(
  db: Db,
  periodo: { inicio: string; fim: string },
): Promise<PedidoDaAnalise[]> {
  const todos: PedidoDaAnalise[] = [];
  for (let de = 0; ; de += PAGINA_ANALISE) {
    const linhas = exigir(
      await db
        .from('pedidos')
        .select(
          'status, valor_centavos, taxa_entrega_centavos, pagamentos, entregador_id, criado_em, saiu_em, entregue_em, entregadores(nome)',
        )
        .gte('criado_em', periodo.inicio)
        .lt('criado_em', periodo.fim)
        .order('criado_em')
        .order('id')
        .range(de, de + PAGINA_ANALISE - 1),
    );
    for (const l of linhas) {
      todos.push({
        status: l.status,
        valorCentavos: l.valor_centavos,
        taxaEntregaCentavos: l.taxa_entrega_centavos,
        pagamentos: paraPagamentos(l.pagamentos),
        entregadorId: l.entregador_id,
        entregadorNome: l.entregadores?.nome ?? null,
        criadoEm: l.criado_em,
        saiuEm: l.saiu_em,
        entregueEm: l.entregue_em,
      });
    }
    if (linhas.length < PAGINA_ANALISE) return todos;
  }
}

export async function obterPedido(db: Db, id: string): Promise<PedidoNaLista | null> {
  const linha = exigirOuNulo(
    await db.from('pedidos').select(COLUNAS_LISTA).eq('id', id).maybeSingle(),
  );
  return linha ? paraPedidoNaLista(linha) : null;
}

/** O que o cliente respondeu no link: se confirmou o recebimento e a avaliação (se houver). */
export interface RespostaDoClienteNoPedido {
  confirmouEm: string | null;
  avaliacao: (AvaliacaoDoCliente & { criadoEm: string }) | null;
}

export async function respostaDoCliente(
  db: Db,
  pedidoId: string,
): Promise<RespostaDoClienteNoPedido | null> {
  const linha = exigirOuNulo(
    await db
      .from('pedidos')
      .select('cliente_confirmou_em, avaliacoes(nota, marcas, comentario, criado_em)')
      .eq('id', pedidoId)
      .maybeSingle(),
  );
  if (!linha) return null;
  const a = linha.avaliacoes;
  return {
    confirmouEm: linha.cliente_confirmou_em,
    avaliacao: a
      ? { nota: a.nota, marcas: a.marcas, comentario: a.comentario, criadoEm: a.criado_em }
      : null,
  };
}

/**
 * Muda o status pelo balcão. O trigger do banco valida a transição e carimba horários. Volta com as
 * colunas da lista: a tela do pedido troca para a linha nova na hora, sem esperar uma releitura.
 */
export async function mudarStatusPedido(
  db: Db,
  id: string,
  status: StatusPedido,
  motivo?: string,
): Promise<PedidoNaLista> {
  const linha = exigir(
    await db
      .from('pedidos')
      .update({ status, ...(motivo !== undefined && { motivo }) })
      .eq('id', id)
      .select(COLUNAS_LISTA)
      .single(),
  );
  return paraPedidoNaLista(linha);
}

/** Troca o entregador. Volta com as colunas da lista (com o nome dele), como `mudarStatusPedido`. */
export async function atribuirEntregador(
  db: Db,
  id: string,
  entregadorId: string | null,
): Promise<PedidoNaLista> {
  // Só enquanto está na loja (o banco também barra a troca depois da saída: pedido_ja_saiu).
  const linha = exigirOuNulo(
    await db
      .from('pedidos')
      .update({ entregador_id: entregadorId })
      .eq('id', id)
      .eq('status', 'pendente')
      .select(COLUNAS_LISTA)
      .maybeSingle(),
  );
  if (!linha) throw new ErroApi('pedido_ja_saiu');
  return paraPedidoNaLista(linha);
}
